import type {AppAbility} from './ability/types'

import {QueryClientProvider} from '@tanstack/react-query'
import {Amplify} from 'aws-amplify'
import {StrictMode} from 'react'
import {createRoot} from 'react-dom/client'
import {Provider} from 'react-redux'

import {ability} from './ability'
import {AbilityContext} from './ability/context'
import {createAwsConfig} from './aws/exports'
import {AmplifyAuthenticator, AuthListener, AuthProvider} from './components/auth'
import {SidebarProvider} from './components/layout/context'
import {Router} from './components/router'
import {setupGlobalErrorHandler} from './lib/amplify-error-handler'
import {queryClient} from './lib/query-client'
import * as sentry from './lib/sentry'
import {setup} from './lib/setup'
import {store} from './store'
import '@aws-amplify/ui-react/styles.css'
import './components/auth/amplify-auth.scss'
import './styles/main.scss'

function App (): JSX.Element {
  return (
      <Provider store={store}>
          <AmplifyAuthenticator
              hideSignUp
              loginMechanisms={['email']}
              services={{
                  async handleSignIn(formData: any) {
                      // Sanitize email: lowercase, trim, and remove all spaces
                      const cleanedUsername = formData.username.toLowerCase().trim().replace(/\s+/g, '')
                      // Sanitize password: only trim whitespace from beginning and end, preserve case
                      const cleanedPassword = formData.password.trim()

                      console.log('🧹 Sanitizing inputs:')
                      console.log('  Email:', formData.username, '->', cleanedUsername)
                      console.log('  Password length:', formData.password.length, '->', cleanedPassword.length)

                      // Import signIn dynamically to avoid circular imports
                      const {signIn} = await import('aws-amplify/auth')
                      return await signIn({username: cleanedUsername, password: cleanedPassword})
                  }
              }}
              formFields={{
                  signIn: {
                      username: {
                          labelHidden: true,
                          placeholder: 'Email'
                      },
                      password: {
                          labelHidden: true,
                          placeholder: 'Password'
                      }
                  },
                  forgotPassword: {
                      username: {
                          labelHidden: true,
                          placeholder: 'Enter your email'
                      }
                  }
              }}
          >
              {({user, signOut}) => (
                  <AuthProvider value={{user: user as any, signOut}}>
                      <StrictMode>

                          <AuthListener/>
                          <QueryClientProvider client={queryClient}>
                              <SidebarProvider>
                                  <AbilityContext.Provider value={ability as AppAbility}>
                                      <Router/>
                                  </AbilityContext.Provider>
                              </SidebarProvider>
                          </QueryClientProvider>
                      </StrictMode>
                  </AuthProvider>
              )}
          </AmplifyAuthenticator>
      </Provider>
  )
}

let container = document.getElementById('app')

if (!container) {
  throw new Error('Container #app not found')
}

let root = createRoot(container)

setup()
  .then(() => {
    // Initialize Amplify with clean configuration after constants are loaded
    const awsConfig = createAwsConfig()
      Amplify.configure(awsConfig)
    console.log('✅ Amplify configured with clean settings')

    // Setup global error handler for Amplify errors
    setupGlobalErrorHandler()
    console.log('✅ Global error handler configured')

    // Initialize Sentry after constants are loaded
    sentry.init()
    console.log('✅ Sentry initialized')

    root.render(<App />)
  })
  .catch(err => {
    console.error(err)
  })
